import React, { useCallback, useEffect, useState } from "react";
import { Fab, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import axios from "../../utils/axios.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import MemoryCard from "./MemoryCard.tsx";
import TilePage, { TilePageAnimationRef } from "../common/TilePage.tsx";

interface MemoriesPageProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

interface MemoryItem {
  id: string;
  memory: string;
}

const MemoriesPage: React.FC<MemoriesPageProps> = ({
  openDrawer = false,
  setOpenDrawer,
}) => {
  const refAnimation = React.useRef<TilePageAnimationRef>(null);
  const animationStopTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (animationStopTimeoutRef.current) {
        clearTimeout(animationStopTimeoutRef.current);
      }
    };
  }, []);

  const fetchMemories = useCallback(async () => {
    const response = await axios.get("/v1/memory/");
    setMemories(response.data.objects || []);
  }, []);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const triggerLogoAnimation = () => {
    refAnimation.current?.start();
    if (animationStopTimeoutRef.current) {
      clearTimeout(animationStopTimeoutRef.current);
    }
    animationStopTimeoutRef.current = setTimeout(() => {
      refAnimation.current?.stop();
      animationStopTimeoutRef.current = null;
    }, 1200);
  };

  const handleCreateMemory = async (memoryText: string) => {
    triggerLogoAnimation();
    await axios.post("/v1/memory/", JSON.stringify({ content: memoryText }));
    setIsCreateDialogOpen(false);
    fetchMemories();
  };

  return (
    <>
      <TilePage
        title={<Trans context="Memories page title">Memories</Trans>}
        subtitle={
          <Trans context="Memories page subtitle">
            Add information to Tally's long-term memory so it can reference them
            in future responses.
          </Trans>
        }
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        animationRef={refAnimation}
        action={
          <Fab
            color="primary"
            aria-label={i18n._(
              msg({
                context:
                  "Accessibility label for creating a memory from memories page",
                message: "Create memory",
              }),
            )}
            onClick={() => setIsCreateDialogOpen(true)}
            sx={{
              position: "absolute",
              right: { xs: 16, sm: 24, md: 28 },
              bottom: { xs: 16, sm: 24, md: 28 },
            }}
          >
            <AddIcon />
          </Fab>
        }
      >
        {memories.map((memory) => (
          <Grid key={memory.id} size={{ xs: 12, sm: 6, md: 6 }}>
            <MemoryCard
              id={memory.id}
              memory={memory.memory}
              onMemoryUpdated={fetchMemories}
              onMemorySaved={triggerLogoAnimation}
            />
          </Grid>
        ))}
      </TilePage>

      <TallyDialog
        open={isCreateDialogOpen}
        title={i18n._(
          msg({
            context: "Button title enabling the user to create a memory",
            message: "Create a memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Header with explanation what creating a memory does in the application.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversations",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title for creation of a memory",
            message: "Create",
          }),
        )}
        onSubmit={handleCreateMemory}
        initialInputValue=""
        handleDialogClose={() => setIsCreateDialogOpen(false)}
      />
    </>
  );
};

export default MemoriesPage;
